const API_BASE = import.meta.env.VITE_API_URL || "";

export type User = {
  id: string;
  email: string;
  username: string;
  name: string;
  role: "ADMIN" | "USER";
};

export type Meeting = {
  id: string;
  title: string;
  type: "DIRECT" | "CONFERENCE";
  status: "SCHEDULED" | "LIVE" | "ENDED";
  roomCode: string;
  hostId: string;
  createdAt: string;
  host?: { id: string; name: string; email: string };
};

export type ChatMessage = {
  id: string;
  meetingId: string;
  type: "TEXT" | "FILE" | "VOTE" | "SYSTEM";
  content: string;
  metadata?: unknown;
  guestName?: string | null;
  createdAt: string;
  senderUser?: { id: string; name: string } | null;
  attachments?: {
    id: string;
    filename: string;
    urlPath: string;
    mimeType: string;
    size: number;
  }[];
  voteResponses?: { id: string; optionId: string; userId?: string | null; guestSession?: string | null }[];
};

function authHeaders(token?: string | null): HeadersInit {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function parseJson<T>(res: Response): Promise<T> {
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || data.message || "Request failed");
  }
  return data as T;
}

export const api = {
  async register(body: { email: string; username: string; password: string; name: string }) {
    return parseJson<{ token: string; user: User }>(
      await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      }),
    );
  },
  async login(body: { login: string; password: string }) {
    return parseJson<{ token: string; user: User }>(
      await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      }),
    );
  },
  async me(token: string) {
    return parseJson<{ user: User }>(
      await fetch(`${API_BASE}/api/auth/me`, { headers: authHeaders(token) }),
    );
  },
  async listMeetings(token: string) {
    return parseJson<{ meetings: Meeting[] }>(
      await fetch(`${API_BASE}/api/meetings`, { headers: authHeaders(token) }),
    );
  },
  async createMeeting(
    token: string,
    body: {
      title: string;
      type: "DIRECT" | "CONFERENCE";
      e2eeEnabled?: boolean;
      recordingEnabled?: boolean;
    },
  ) {
    return parseJson<{ meeting: Meeting; guestInviteUrl: string; guestInviteToken: string }>(
      await fetch(`${API_BASE}/api/meetings`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify(body),
      }),
    );
  },
  async getMeeting(token: string, id: string) {
    return parseJson<{ meeting: Meeting & { invites: unknown[] } }>(
      await fetch(`${API_BASE}/api/meetings/${id}`, { headers: authHeaders(token) }),
    );
  },
  async createGuestInvite(token: string, meetingId: string) {
    return parseJson<{ url: string }>(
      await fetch(`${API_BASE}/api/meetings/${meetingId}/invites/guest`, {
        method: "POST",
        headers: authHeaders(token),
      }),
    );
  },
  async inviteUser(token: string, meetingId: string, userId: string) {
    return parseJson<{ invite: unknown }>(
      await fetch(`${API_BASE}/api/meetings/${meetingId}/invites/user`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ userId }),
      }),
    );
  },
  async searchUsers(token: string, q: string) {
    return parseJson<{ users: User[] }>(
      await fetch(`${API_BASE}/api/users?q=${encodeURIComponent(q)}`, {
        headers: authHeaders(token),
      }),
    );
  },
  async previewInvite(token: string) {
    return parseJson<{
      meeting: { id: string; title: string; type: string; host: { name: string } };
      requiresLogin: boolean;
    }>(await fetch(`${API_BASE}/api/meetings/join/preview/${token}`));
  },
  async uploadFile(token: string, meetingId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API_BASE}/api/files/${meetingId}/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    return parseJson<{ message: ChatMessage }>(res);
  },
  async uploadFileGuest(
    meetingId: string,
    file: File,
    inviteToken: string,
    guestName: string,
  ) {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${API_BASE}/api/files/guest/${meetingId}/upload`, {
      method: "POST",
      headers: {
        "X-Invite-Token": inviteToken,
        "X-Guest-Name": guestName,
      },
      body: form,
    });
    return parseJson<{ message: ChatMessage }>(res);
  },
  async startLiveKitEgress(token: string, meetingId: string) {
    return parseJson<{ egressId: string }>(
      await fetch(`${API_BASE}/api/livekit/${meetingId}/egress/start`, {
        method: "POST",
        headers: authHeaders(token),
      }),
    );
  },
  async stopLiveKitEgress(token: string, meetingId: string) {
    return parseJson<{ ok: boolean }>(
      await fetch(`${API_BASE}/api/livekit/${meetingId}/egress/stop`, {
        method: "POST",
        headers: authHeaders(token),
      }),
    );
  },
};

export function uploadsUrl(path: string) {
  if (path.startsWith("http")) return path;
  return `${API_BASE}${path}`;
}

export function socketUrl() {
  return import.meta.env.VITE_API_URL || window.location.origin;
}
